import { Children, useEffect, useState } from 'react';

import { ErrorPage, LoadingPageV2, StalledPage } from '../pages';
import { useAuthState } from '../providers/authState';
import { useGlobalLoader } from '../providers/globalLoader';
import { useTranslation } from '../providers/i18next';

const TIMEOUT = 20000;
/**
 * Extends a ReactRouter Route component with a few checks.
 *  - if there has been some sort of initialization error with the auth subsystems,
 *    display a takeover Error page
 *  - if the auth subsystems are still being initialized,
 *    display a takeover Loading page
 *  - otherwise, should the component intended for this route
 * @returns {React.Component}
 */

interface Props {
  loadingText?: string;
  children: typeof Children | JSX.Element;
}

const LoadingRouteV2 = ({ children, loadingText }: Props) => {
  const { isLoading, isFailed, isReady, isUserLoading, authError } =
    useAuthState();
  const [isTimeout, setIsTimeout] = useState(false);
  const isFinished = !isLoading && isReady && !isUserLoading;
  const isError = isFailed && authError;
  const { isSkipLoadingTimeout } = useGlobalLoader();
  const { txn } = useTranslation();

  // taking too long, give up and offer the user a way out.
  // See the note in LoadingRoute: without a dependency array this timer was
  // reset by every render, so a render loop kept it from ever firing (MP-6664).
  useEffect(() => {
    if (isFinished) {
      return;
    }
    const timer = setTimeout(() => {
      if (!isSkipLoadingTimeout()) {
        setIsTimeout(true);
      }
    }, TIMEOUT);
    return () => clearTimeout(timer);
    // isSkipLoadingTimeout is a stable getter from the global loader context
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFinished]);

  if (isError) {
    // something went wrong during initialization or first user lookup.
    return <ErrorPage error={authError} />;
  } else if (isFinished) {
    // once ready, this will never be unready so this won't rerender
    // app is ready. show intended component.
    return <>{children}</>;
  } else if (isTimeout) {
    // initialization never settled. reload once, then let the user retry.
    return <StalledPage />;
  } else {
    // waiting for firebase to initialize or perform first user lookup, show loading.
    return <LoadingPageV2 loadingText={loadingText ? txn(loadingText) : ''} />;
  }
};

export default LoadingRouteV2;
